/**
 * The support sheet's pure half: no imports, so scripts/check-giving.js runs
 * this exact file for $0. It holds the sheet's five fields in
 * meta/publicConfig and their rules (the server's are the same:
 * functions/limits.js resolvePublicConfig, held to one table by the check),
 * the two links to The Net's Pushpay page, the Facebook and Instagram browser
 * test, how long the asks rest, and every word the sheet says.
 * utils/giving.js keeps the live state; components/GiveSheet.js draws it.
 */

/** The church's own giving page: the plain link's default, and "Other ways to give". */
export const GIVE_PAGE = 'https://www.thenet.org/give/';
// The plain link before the live config arrives: straight to The Net's Pushpay
// page, General fund locked, staff note (the same as meta/publicConfig giveUrl).
export const PUSHPAY_DIRECT = 'https://pushpay.com/g/thenethuntsville?r=No&fnd=General&fndv=lock&nt=Ask%20Pastor%20Rob';

/**
 * The sheet's fields when meta/publicConfig says nothing (each one changes
 * with scripts/set-public-config.js, no deploy):
 *   giveSheet    0: every entry is the plain link (the kill switch); 1: the sheet
 *   pushpayUrl   The Net's Pushpay listing, with nothing after it
 *   giveNote     the note stored with each gift, seen only in Pushpay admin ('' for none)
 *   giveFund     the fund both buttons lock: General, the tax-deductible one ('' for none)
 *   answerCents  what one answer costs in AI computing, in whole cents (0: no figure)
 */
export const DEFAULTS = {
  giveSheet: 0,
  pushpayUrl: 'https://pushpay.com/g/thenethuntsville',
  giveNote: 'Ask Pastor Rob',
  giveFund: 'General',
  answerCents: 4,
};

// Pushpay only, a listing with no query or fragment: a pasted link can never
// slip in an amount, r=Monthly or someone's name.
const PUSHPAY_BASE = /^https:\/\/pushpay\.com\/g\/[A-Za-z0-9_-]{2,64}$/;
// Plain words: no @, no quotes, no apostrophes. The doc is public.
const NOTE = /^[A-Za-z0-9](?:[A-Za-z0-9 .-]{0,38}[A-Za-z0-9.])?$/;
// The exact Pushpay fund name (or its key), once The Net has created it.
const FUND = /^[A-Za-z0-9](?:[A-Za-z0-9 .-]{0,58}[A-Za-z0-9.])?$/;
const RANGES = { giveSheet: [0, 1], answerCents: [0, 50] };

/** Only an https link ever becomes an href: the doc is public, so be strict (functions/limits.js has the same). */
export function isSafeGiveUrl(url) {
  if (typeof url !== 'string' || url.length > 500 || !/^https:\/\/[^\s"'<>\\]+$/.test(url)) return false;
  try {
    return new URL(url).protocol === 'https:';
  } catch (e) {
    return false;
  }
}

/** A number from the doc (a number, or digits in a string), or null: functions/limits.js numberFrom. */
function numberFrom(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** A whole number clamped into [min, max], or null when there is none. */
export function wholeNumber(value, [min, max]) {
  const n = numberFrom(value);
  return n === null ? null : Math.min(max, Math.max(min, Math.floor(n)));
}

const textField = (value, rule) => (typeof value === 'string' && (value === '' || rule.test(value)) ? value : null);

/**
 * The sheet's fields in `source` that are there and pass their rule (the
 * numbers clamped). What the browser takes from the doc or an answer's
 * metadata: a field that is missing or bad never moves what it already has.
 */
export function validGiving(source) {
  const s = source && typeof source === 'object' ? source : {};
  const out = {};
  for (const key of Object.keys(RANGES)) {
    const n = wholeNumber(s[key], RANGES[key]);
    if (n !== null) out[key] = n;
  }
  if (typeof s.pushpayUrl === 'string' && PUSHPAY_BASE.test(s.pushpayUrl)) out.pushpayUrl = s.pushpayUrl;
  const note = textField(s.giveNote, NOTE);
  if (note !== null) out.giveNote = note;
  const fund = textField(s.giveFund, FUND);
  if (fund !== null) out.giveFund = fund;
  return out;
}

/** The five fields as the server hands them out: each missing or bad one falls back to its default. */
export function resolveGiving(doc) {
  return { ...DEFAULTS, ...validGiving(doc) };
}

// encodeURIComponent, with ! ' ( ) * encoded too. Never URLSearchParams: it
// writes a space as "+", and Pushpay asks for %20 in fund names.
const enc = (value) => encodeURIComponent(String(value))
  .replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);

/**
 * "Give once" ({ monthly: false }) or "Give monthly" ({ monthly: true }):
 * The Net's Pushpay page with, in this order, r (always sent, so a default on
 * the church's listing can never surprise anyone), the fund locked (fnd,
 * fndv=lock) and the staff note (nt). rcv is left off, so Pushpay's own
 * once-or-monthly choice still shows. Never an amount (the field starts
 * blank), nothing about the person, nothing from a conversation
 * (https://docs.pushpay.io/giving-v1/docs/preconfigured-giving-link.md).
 * Anything odd falls back to the bare listing.
 */
export function buildGiveLink(cfg, { monthly = false } = {}) {
  const g = resolveGiving(cfg);
  const parts = [['r', monthly ? 'Monthly' : 'No']];
  if (g.giveFund) parts.push(['fnd', g.giveFund], ['fndv', 'lock']);
  if (g.giveNote) parts.push(['nt', g.giveNote]);
  const url = `${g.pushpayUrl}?${parts.map(([k, v]) => `${k}=${enc(v)}`).join('&')}`;
  return isSafeGiveUrl(url) ? url : g.pushpayUrl;
}

/** "Other ways to give": the plain link, or the church's own page when the plain link is Pushpay already. */
export function otherWaysUrl(giveUrl) {
  if (!isSafeGiveUrl(giveUrl)) return GIVE_PAGE;
  try {
    return /(^|\.)pushpay\.com$/i.test(new URL(giveUrl).hostname) ? GIVE_PAGE : giveUrl;
  } catch (e) {
    return GIVE_PAGE;
  }
}

/** Facebook's and Instagram's own browsers (Messenger's too) name themselves in the user agent. */
export function inAppBrowser(ua) {
  return /\bFBAN\/|\bFBAV\/|\bFB_IAB\b|\bFBIOS\b|\bInstagram\b/i.test(String(ua || ''));
}

// How long the asks stay quiet on this device. Constants, not knobs: safety
// timing should never be one console edit away.
export const HOUR_MS = 60 * 60 * 1000;
export const HOLD_MS = 24 * HOUR_MS; // a crisis card, or the server's givingQuiet
export const ALREADY_MS = 90 * 24 * HOUR_MS; // "I already give to The Net"
export const RETURN_NOTE_MS = 30 * 60 * 1000; // the thank-you note after a reload
export const WINDOW_FALLBACK_MS = 5 * HOUR_MS; // a question window, when its end isn't known

// This device's memory (utils/giving.js), never sent anywhere.
export const REST_KEY = 'apr-give-rest'; // localStorage: until when the asks rest
export const HOLD_KEY = 'apr-give-hold'; // localStorage: until when a crisis hold runs
export const OUT_KEY = 'apr-give-out'; // sessionStorage: when this tab last went to Pushpay

/** A time kept in `store` (localStorage or sessionStorage) under `key`, or 0. Never throws. */
export function readTime(store, key) {
  try {
    const n = Number(store ? store.getItem(key) : null);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch (e) {
    return 0;
  }
}

/** Keeps a time in `store`; false when the store refuses (blocked, full, a private window). */
export function writeTime(store, key, value) {
  try {
    store.setItem(key, String(value));
    return true;
  } catch (e) {
    return false;
  }
}

/** Until when a decline or a visit to Pushpay rests the asks: this window's end, else five hours. */
export function restUntilWindowEnd(now, resetAt) {
  const end = Number(resetAt);
  return Number.isFinite(end) && end > now ? end : now + WINDOW_FALLBACK_MS;
}

/** What the asks may do now: 'hold' (a crisis, which beats everything), 'rest', or 'ask'. */
export function askState(now, { holdUntil = 0, restUntil = 0 } = {}) {
  if (now < holdUntil) return 'hold';
  if (now < restUntil) return 'rest';
  return 'ask';
}

/** Back from Pushpay after a reload, within half an hour of the tap: the note is due. */
export function returnNoteDue(now, outAt) {
  return outAt > 0 && now >= outAt && now - outAt < RETURN_NOTE_MS;
}

// What an answer offers when AI Rob heard someone struggling: 988, 911,
// 741741, findhelp.org, SAMHSA's National Helpline, the abuse hotlines,
// Poison Control (functions/guards.js HELP_OFFERED; scripts/check-giving.js
// holds the two to one table).
// The character before a number is matched, not looked behind at: Safari
// before 16.4 refuses a whole script with a lookbehind in it, and the app
// would be blank on an older iPhone (scripts/check-scripture.js).
const HELP_OFFERED = /(?:^|[^\d-])9[-.]?8[-.]?8(?!\d|[-.]\d)|(?:^|[^\d-])9[-.]?1[-.]?1(?!\d|[-.]\d)|(?:^|\D)741[-.\s]?741(?!\d)|(?:^|\D)(?:1[-.\s]?)?\(?800\)?[-.\s]?(?:799[-.\s]?(?:7233|safe)|422[-.\s]?4453|4[-.\s]?a[-.\s]?child|662[-.\s]?(?:4357|help)|222[-.\s]?1222)\b|\bfindhelp\.org\b|\bsamhsa\b|\bpoison\s+control\b|\bcrisis\s+(?:text\s+)?line\b|\bdomestic\s+violence\s+hotline\b/i;

/**
 * Whether an answer offered help. Then the conversation is a hard season,
 * and quiet: no support line under that answer, no card, no sheet (the
 * server's givingQuiet says the same on the next turn).
 */
export function offersHelp(text) {
  return typeof text === 'string' && HELP_OFFERED.test(text.toLowerCase().replace(/[‘’ʼ´`′＇‛]/g, "'").replace(/[^\S\n]+/g, ' '));
}

/** Every supportEvery-th question of a window gets the support line (the server counts them). */
export function isSupportTurn(windowCount, cfg) {
  const every = cfg ? cfg.supportEvery : 0;
  const n = Number(windowCount);
  return every > 0 && Number.isInteger(n) && n > 0 && n % every === 0;
}

/**
 * The cost card's middle sentence, from what this person's questions have
 * cost so far (the server's figure, in dollars): "$0.42", "less than a dime",
 * or, when the figure isn't known, what an answer costs (answerCents; none at 0).
 */
export function costSentence(spentUsd, cents = DEFAULTS.answerCents) {
  const usd = Number(spentUsd);
  if (spentUsd == null || !Number.isFinite(usd) || usd < 0) {
    return cents > 0 ? `The AI computing behind your questions costs about ${cents}¢ a question.` : '';
  }
  if (usd < 0.1) return 'The AI computing behind your questions so far has cost less than a dime.';
  const amount = usd >= 10 ? `$${Math.round(usd)}` : `$${usd.toFixed(2)}`;
  return `The AI computing behind your questions so far has cost about ${amount}.`;
}

/**
 * Every word the sheet says, in one place (check-giving.js reads it all):
 * full stops, no exclamation marks, no church-insider words. Rob owns the
 * giving wording (docs/LIMITS_AND_GIVING.md).
 */
export const COPY = {
  title: 'Support Ask Pastor Rob',
  close: 'Close',
  // Only when opened from the card that says the window's questions are used up.
  limit: (time) => (time
    ? `A gift doesn’t add questions. Yours refresh at ${time} either way.`
    : 'A gift doesn’t add questions. Yours refresh in a few hours either way.'),
  // The Net speaking, so it is set in the serif.
  lead: (cents) => `Ask Pastor Rob is free for everyone, and it always will be. ${cents > 0
    ? `The Net, the church in Huntsville behind it, pays about ${cents}¢ in AI computing for each answer.`
    : 'The Net, the church in Huntsville behind it, pays for every answer.'} If it has helped you, a gift helps keep it free for the next person who asks.`,
  giveOnce: 'Give once',
  giveMonthly: 'Give monthly',
  opens: 'You’ll choose the amount on Pushpay, The Net’s secure giving page.',
  // Dropped where the host decides where a link opens (Facebook, Instagram, a home-screen install).
  opensTab: 'It opens in a new tab.',
  wait: 'You can continue as soon as AI Rob finishes this answer.',
  hint: 'In Facebook and Instagram, Apple Pay and Google Pay may not show. To use one, open the next page in your browser from the ⋯ menu.',
  // The fund is named only once it is one of its own, not The Net's General fund.
  fine: (fund) => `${fund && fund !== DEFAULTS.giveFund
    ? `Gifts go to the ${fund} fund at The Net Community Church in Huntsville (The Net Org, Inc. on Pushpay).`
    : 'Gifts go to The Net Community Church in Huntsville (The Net Org, Inc. on Pushpay), which pays for Ask Pastor Rob.'} You’ll get a receipt by email. Your gift is never connected to your conversations, and it doesn’t change how many questions you can ask.`,
  otherWays: 'Other ways to give',
  notNow: 'Not now',
  already: 'I already give to The Net',
  // Back from Pushpay: thanks for the thought, never a claim that a gift went through.
  returnedTitle: 'Thank you for thinking of Ask Pastor Rob.',
  returnedText: 'If you gave, you’ll get a receipt by email. Ask Pastor Rob stays free for everyone, either way.',
  again: 'Didn’t open? Go to Pushpay in this tab',
  alreadyTitle: 'Thank you for giving to The Net.',
  alreadyText: 'We’ll stop asking on this device for the next 90 days.',
  done: 'Done',
  // The conversation's last line after a reload from Pushpay (ChatView): local, never saved or sent.
  returnNote: 'Thank you for thinking of Ask Pastor Rob. If you gave, you’ll get a receipt by email.',
};

/**
 * What the first-party counter calls each press (data-track; functions/
 * analytics.js groups them). Opening the sheet names where it was opened;
 * leaving for a giving page lands in the donate group, credited to the
 * campaign; the rest are plain presses, with no "give" or "donate" in them,
 * so one intent is never counted twice.
 */
export const TRACK_OPEN = {
  answer: 'Support sheet (answer line)',
  card: 'Support sheet (cost card)',
  limit: 'Support sheet (limit card)',
  menu: 'Support sheet (menu)',
  rail: 'Support sheet (rail)',
};

export const TRACK = {
  once: 'Donate',
  monthly: 'Donate monthly',
  otherWays: 'Donate (other ways)',
  notNow: 'Support sheet: not now',
  close: 'Support sheet: close',
  done: 'Support sheet: done',
  again: 'Support sheet: Pushpay again',
  already: 'Support sheet: already supporting',
};
